import { CommonModule } from '@angular/common'
import { Component, CUSTOM_ELEMENTS_SCHEMA, effect, input, ViewEncapsulation } from '@angular/core'
import { MatDialogRef } from '@angular/material/dialog'
import { MatDividerModule } from '@angular/material/divider'
import { MatIconModule } from '@angular/material/icon'
import { Router } from '@angular/router'
import { MarkdownModule } from 'ngx-markdown'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { Quiz } from '../../models/main/quiz'
import { DdcButtonNOneActionComponent } from '../ddc-button-n-one-action/ddc-button-n-one-action.component'
import { DdcPrivacyComponent } from '../ddc-privacy/ddc-privacy.component'
import { DdcProfilePicComponent } from '../ddc-profile-pic/ddc-profile-pic.component'
import { DdcTextGradientComponent } from '../ddc-text-gradient/ddc-text-gradient.component'

@Component({
  selector: 'ddc-read-quiz-mobile-sheet',
  templateUrl: './ddc-read-quiz-mobile-sheet.component.html',
  styleUrls: ['./ddc-read-quiz-mobile-sheet.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    AngularMaterialsModules,
    CommonModule,
    MatIconModule,
    MatDividerModule,
    DdcPrivacyComponent,
    DdcTextGradientComponent,
    DdcProfilePicComponent,
    MarkdownModule,
    DdcButtonNOneActionComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DdcReadQuizMobileSheetComponent {
  quiz = input<Quiz>(undefined)

  // Profile picture properties
  _dynamicRoute: string = ''
  _isProfilePicLoading: boolean = false
  _userProfilePic: string = ''

  _isDarkTheme = false
  _isLightTheme = true
  _isPaperTheme = false

  constructor(
    private readDialogRef: MatDialogRef<DdcReadQuizMobileSheetComponent>,
    private _themeService: ThemeService,
    private router: Router,
  ) {
    this._themeService.theme$.subscribe(theme => {
      this._isDarkTheme = theme === 'dark-theme'
      this._isLightTheme = theme === 'light-theme'
      this._isPaperTheme = theme === 'paper-theme'
    })

    // React to quiz changes
    effect(() => {
      this.setupProfilePicData()
    })
  }

  private setupProfilePicData(): void {
    const currentQuiz = this.quiz()
    if (currentQuiz?.owner) {
      this._dynamicRoute = `/users/${currentQuiz.owner.username || ''}`
      this._userProfilePic = currentQuiz.owner.profile_pic || ''
      this._isProfilePicLoading = false
    }
  }

  protected onProfilePicClick(): void {
    this.readDialogRef.close()
  }

  // Helper methods for quiz stats
  getQuestionCount(): number {
    return this.quiz()?.questions?.length || 0
  }

  getTimeDisplay(): string {
    const quiz = this.quiz()
    if (!quiz) return '-'

    if (quiz.completed_time) {
      return this.formatTime(quiz.completed_time)
    }
    return 'Not completed'
  }

  private formatTime(seconds: number | string): string {
    const numSeconds = typeof seconds === 'string' ? parseInt(seconds) : seconds
    if (isNaN(numSeconds)) return '-'

    const hours = Math.floor(numSeconds / 3600)
    const minutes = Math.floor((numSeconds % 3600) / 60)
    const secs = numSeconds % 60

    const parts: string[] = []
    if (hours > 0) parts.push(`${hours}h`)
    if (minutes > 0) parts.push(`${minutes}m`)
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`)

    return parts.join(' ')
  }

  routeToQuiz(): void {
    this.router.navigate(['/quiz', this.quiz()?.id])
    this.readDialogRef.close()
  }

  getQuestionLevels(): Array<{
    label: string
    count: number
    percentage: number
    color: string
    colorClass: string
  }> {
    const quiz = this.quiz()
    if (!quiz?.questions || quiz.questions.length === 0) return []

    const total = quiz.questions.length
    const easyCount = quiz.questions.filter(q => q.level === 'easy').length
    const intermediateCount = quiz.questions.filter(q => q.level === 'intermediate').length
    const hardCount = quiz.questions.filter(q => q.level === 'hard').length

    return [
      {
        label: 'Easy',
        count: easyCount,
        percentage: (easyCount / total) * 100,
        color: 'accent',
        colorClass: 'easy-bar',
      },
      {
        label: 'Intermediate',
        count: intermediateCount,
        percentage: (intermediateCount / total) * 100,
        color: 'primary',
        colorClass: 'intermediate-bar',
      },
      {
        label: 'Hard',
        count: hardCount,
        percentage: (hardCount / total) * 100,
        color: 'warn',
        colorClass: 'hard-bar',
      },
    ].filter(level => level.count > 0)
  }

  formatDeadline(deadline: number): string {
    if (!deadline) return '-'
    const days = Math.ceil(deadline / (1000 * 60 * 60 * 24))
    if (days <= 0) return 'Expired'
    if (days === 1) return '1 day'
    return `${days} days`
  }

  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    } catch {
      return '-'
    }
  }
}
